// IM-XPR-005 (IM-UX-122, IM-UI-103) — Componente ÚNICO de item do battlecard, usado em todas as abas. Três camadas:
//   1. decisão: conclusão, implicação comercial, status visível, confiança e só as ações primárias (Confirmar e, conforme
//      o caso, Rejeitar ou Manter como hipótese); o resto fica no menu "Mais ações";
//   2. explicação (sob demanda): justificativa em linguagem natural, as três dimensões do status, evidências específicas
//      do item (diretas, parciais, herdadas da regra) e as demais páginas da mesma análise à parte;
//   3. auditoria técnica (área avançada): regra, ids, versão, origem e linhagem.
import { createContext, useContext, useState, type ReactNode } from 'react';
import {
  BATTLECARD_CLASSIFICATION_LABEL, BATTLECARD_SECTIONS, BATTLECARD_VALIDATION_LABEL, EVIDENCE_RELATION_LABEL, VISIBLE_STATUS_LABEL, VISIBLE_STATUS_TONE,
  explainItem, gameReading, itemEvidence, statusDimensions, visibleStatus, type BattlecardDetailDto, type BattlecardItemDto, type CardNames,
} from '@intelimove/contracts';
import { useSubmitOnce } from '../../components/Recommendation';
import { useToast } from '../../components/Toast';
import { battlecardApi, CONF_TXT, fmtDate, host, safeHref, VALIDATION_ACTOR_TXT } from '../../data/battlecards';

export type ModalKind = 'edit' | 'history' | 'archive';
export interface CardCtxValue {
  d: BattlecardDetailDto; editable: boolean; names: CardNames;
  open: (kind: ModalKind, item: BattlecardItemDto) => void;
  /** Item atualizado pela API (sem recarregar a tela inteira). */
  replace: (item: BattlecardItemDto) => void;
  refresh: () => void;
  /** Última decisão (para "Desfazer"). */
  lastDecision: { item: BattlecardItemDto; label: string } | null;
  setLastDecision: (x: { item: BattlecardItemDto; label: string } | null) => void;
}
export const CardCtx = createContext<CardCtxValue | null>(null);
export const useCard = () => useContext(CardCtx)!;

export const sectionLabel = (k: string) => BATTLECARD_SECTIONS.find((s) => s.key === k)?.label ?? k;
const HYPOTHESIS_LIKE = new Set(['ganha_quando', 'perde_quando', 'perguntas', 'argumentos', 'objecoes', 'comparacao', 'sobreposicoes', 'lacunas', 'forcas', 'fragilidades', 'riscos', 'oportunidades']);

export function StatusChip({ i }: { i: BattlecardItemDto }) {
  const st = visibleStatus(i);
  return <span className={`tag ${VISIBLE_STATUS_TONE[st]}`}>{VISIBLE_STATUS_LABEL[st]}</span>;
}

/** Ações do item: primárias visíveis, o resto no menu secundário. */
export function ItemActions({ i, compact = false }: { i: BattlecardItemDto; compact?: boolean }) {
  const { d, editable, open, replace, refresh, setLastDecision } = useCard();
  const toast = useToast();
  const { busy, run: once } = useSubmitOnce();
  if (!editable) return compact ? null : <div className="bc-actions"><button className="link-btn" onClick={() => open('history', i)}>Histórico</button></div>;
  const decide = (decision: 'CONFIRM' | 'REJECT' | 'RESET' | 'KEEP_HYPOTHESIS', label: string, ok: string) => once(async () => {
    try { const x = await battlecardApi.decide(d.id, i, decision); toast(ok); replace(x); setLastDecision(decision === 'RESET' ? null : { item: x, label }); } catch (e) { toast((e as Error).message, 'error'); }
  });
  const active = i.status === 'ACTIVE';
  const gap = i.classification === 'NAO_ENCONTRADO';
  const hypo = HYPOTHESIS_LIKE.has(i.section) || i.classification === 'HIPOTESE';
  // IM-XPR-006 (IM-UX-141): verificado em fonte oficial ou declarado pela empresa não pede confirmação manual — a
  // equipe só corrige ou contesta se estiver errado.
  const settled = i.validationState === 'AUTO_VALIDATED' && ['VERIFICADO', 'DECLARADO', 'CONFIRMADO'].includes(visibleStatus(i));
  const canConfirm = active && !gap && !settled && i.reviewStatus !== 'CONFIRMED';
  const second: 'KEEP_HYPOTHESIS' | 'REJECT' | null = !active || gap ? null : hypo ? (i.reviewStatus !== 'HYPOTHESIS' && i.validationState === 'NEEDS_REVIEW' ? 'KEEP_HYPOTHESIS' : i.reviewStatus !== 'REJECTED' ? 'REJECT' : null) : i.reviewStatus !== 'REJECTED' ? 'REJECT' : null;
  const name = i.content.slice(0, 60);
  return (
    <div className="bc-actions">
      {canConfirm && <button className="small-action" disabled={busy} onClick={() => void decide('CONFIRM', 'confirmado', 'Item confirmado por você.')}>Confirmar</button>}
      {settled && active && <button className="small-action" onClick={() => open('edit', i)}>Corrigir</button>}
      {settled && active && i.reviewStatus !== 'REJECTED' && <button className="small-action" disabled={busy} onClick={() => void decide('REJECT', 'contestado', 'Item contestado: ele sai do battlecard e a decisão fica no histórico.')}>Contestar</button>}
      {second === 'KEEP_HYPOTHESIS' && <button className="small-action" disabled={busy} onClick={() => void decide('KEEP_HYPOTHESIS', 'mantido como hipótese', 'Mantido como hipótese.')}>Manter como hipótese</button>}
      {second === 'REJECT' && !settled && <button className="small-action" disabled={busy} onClick={() => void decide('REJECT', 'rejeitado', 'Item rejeitado. Ele sai do battlecard e não volta como pendente.')}>Rejeitar</button>}
      {i.status === 'ARCHIVED' && <button className="small-action" disabled={busy} onClick={() => void once(async () => { try { replace(await battlecardApi.restore(d.id, i)); toast('Item restaurado.'); } catch (e) { toast((e as Error).message, 'error'); } })}>Restaurar</button>}
      <details className="bc-menu">
        <summary aria-label={`Mais ações: ${name}`}>Mais ações</summary>
        <div className="bc-menu-list">
          {active && <button type="button" onClick={() => open('edit', i)}>Editar</button>}
          {second === 'KEEP_HYPOTHESIS' && i.reviewStatus !== 'REJECTED' && <button type="button" disabled={busy} onClick={() => void decide('REJECT', 'rejeitado', 'Item rejeitado.')}>Rejeitar</button>}
          {second === 'REJECT' && hypo && i.reviewStatus !== 'HYPOTHESIS' && <button type="button" disabled={busy} onClick={() => void decide('KEEP_HYPOTHESIS', 'mantido como hipótese', 'Mantido como hipótese.')}>Manter como hipótese</button>}
          {active && i.reviewStatus !== 'PENDING' && <button type="button" disabled={busy} onClick={() => void decide('RESET', '', 'Decisão desfeita: vale a decisão do sistema.')}>Desfazer decisão</button>}
          {active && <button type="button" onClick={() => open('archive', i)}>Arquivar</button>}
          {i.origin === 'USER' && <button type="button" disabled={busy} onClick={() => { if (window.confirm('Excluir este item incluído pela equipe? O histórico continua registrado.')) void once(async () => { try { await battlecardApi.remove(d.id, i); toast('Item excluído.'); refresh(); } catch (e) { toast((e as Error).message, 'error'); } }); }}>Excluir</button>}
          <button type="button" onClick={() => open('history', i)}>Histórico</button>
        </div>
      </details>
    </div>
  );
}

/** Camada 2: explicação humana, dimensões e evidências específicas. Camada 3 (técnica) aninhada e fechada. */
export function ItemExplanation({ i, names, defaultOpen = false }: { i: BattlecardItemDto; names: CardNames; defaultOpen?: boolean }) {
  const ev = itemEvidence(i);
  const dims = statusDimensions(i);
  return (
    <details className="bc-why-layer" open={defaultOpen || undefined}>
      <summary>Por que o InteliMove classificou assim?</summary>
      <p className="bc-explain">{explainItem(i, names)}</p>
      <dl className="bc-dims">
        <dt>Fonte</dt><dd>{dims.source}</dd>
        <dt>Verificação</dt><dd>{dims.check}</dd>
        <dt>Decisão da equipe</dt><dd>{dims.decision}</dd>
      </dl>
      {(ev.shown.length > 0 || i.sourceUrl) && (
        <div className="bc-ev-block">
          <h4>Evidências deste ponto ({ev.shown.length + (i.sourceUrl ? 1 : 0)})</h4>
          <ul className="bc-ev">
            {ev.shown.map(({ evidence: e, relation }) => (
              <li key={e.id}>
                {safeHref(e.url) ? <a href={e.url!} target="_blank" rel="noopener noreferrer">{e.title || host(e.url!)}</a> : <span>{e.title ?? 'Fonte sem endereço público'}</span>}
                <span className="cell-sub">{EVIDENCE_RELATION_LABEL[relation]}{e.url ? ` · ${host(e.url)}` : ''}{e.firstParty ? ' · site do próprio concorrente' : ''} · {e.dateKind === 'PUBLICADA' ? 'publicada' : e.dateKind === 'COLETADA' ? 'coletada' : 'registrada'} em {fmtDate(e.date)}</span>
                {e.excerpt && <blockquote>{e.excerpt}</blockquote>}
              </li>
            ))}
            {i.sourceUrl && <li><a href={i.sourceUrl} target="_blank" rel="noopener noreferrer">{host(i.sourceUrl)}</a><span className="cell-sub">fonte informada pela equipe</span></li>}
          </ul>
        </div>
      )}
      {!ev.shown.length && !i.sourceUrl && i.classification !== 'NAO_ENCONTRADO' && <p className="cell-sub">Sem evidência que sustente diretamente este ponto{i.origin === 'RULE' || i.origin === 'CONTEXT' ? ' — é interpretação a partir de outros pontos ou do contexto da sua empresa' : ''}.</p>}
      {ev.unrelated.length > 0 && (
        <details className="bc-ev-other"><summary>Outras páginas da mesma análise ({ev.unrelated.length}) — não sustentam diretamente este ponto</summary>
          <ul className="bc-ev">{ev.unrelated.map(({ evidence: e }) => <li key={e.id}>{safeHref(e.url) ? <a href={e.url!} target="_blank" rel="noopener noreferrer">{e.title || host(e.url!)}</a> : e.title}</li>)}</ul>
        </details>
      )}
      <details className="bc-tech-layer">
        <summary>Ver detalhes técnicos</summary>
        <dl className="kv small">
          <dt>Natureza registrada</dt><dd>{BATTLECARD_CLASSIFICATION_LABEL[i.classification]}</dd>
          <dt>Estado de validação</dt><dd>{BATTLECARD_VALIDATION_LABEL[i.validationState]}</dd>
          <dt>Decisão do sistema</dt><dd>{i.validation.state ? `${BATTLECARD_VALIDATION_LABEL[i.validation.state]} — ${i.validation.reason ?? ''}` : 'ainda não avaliado'}</dd>
          {i.validation.rule && <><dt>Regra</dt><dd><code>{i.validation.rule}</code>{i.validation.actor ? ` · ${VALIDATION_ACTOR_TXT[i.validation.actor]}` : ''}{i.validation.at ? ` · ${fmtDate(i.validation.at, true)}` : ''}</dd></>}
          {typeof i.originRef?.rule === 'string' && <><dt>Regra de composição</dt><dd><code>{i.originRef.rule}</code></dd></>}
          <dt>Origem</dt><dd>{i.originLabel}</dd>
          <dt>Evidências vinculadas</dt><dd>{i.evidences.length} (usadas na validação: {i.validation.evidenceIds.length})</dd>
          <dt>Linhagem · versão do item</dt><dd><code>{i.lineageId}</code> · {i.version}</dd>
          <dt>Identificador</dt><dd><code>{i.id}</code></dd>
          {i.edited && <><dt>Texto original</dt><dd>{i.original.content}</dd></>}
        </dl>
      </details>
    </details>
  );
}

/** Item completo (camadas 1 a 3). `game` usa a frase com sujeito explícito do jogo comercial. */
export function ItemCard({ i, game = false, showSection = false, extra, defaultExplain = false }: { i: BattlecardItemDto; game?: boolean; showSection?: boolean; extra?: ReactNode; defaultExplain?: boolean }) {
  const { names } = useCard();
  const g = game ? gameReading(i, names) : null;
  const implication = g ? g.whyItMatters : i.section === 'objecoes' ? null : i.detail && !/^Derivado de:/.test(i.detail) ? i.detail : null;
  return (
    <li className={`bc-card${i.status === 'ARCHIVED' ? ' archived' : ''}${visibleStatus(i) === 'NAO_IDENTIFICADO' ? ' missing' : ''}`}>
      <div className="bc-card-top">
        <p className="bc-card-text">{showSection && <span className="cell-sub">{sectionLabel(i.section)} · </span>}{g ? g.sentence : i.content}</p>
        <StatusChip i={i} />
      </div>
      {implication && <p className="bc-implication">{g ? <b>Por que importa: </b> : null}{implication}</p>}
      {g?.howToAct && <p className="bc-response"><b>Como agir:</b> {g.howToAct}</p>}
      {!g && i.response && <p className="bc-response"><b>Resposta recomendada:</b> {i.response}</p>}
      {i.observation && <p className="bc-note"><b>Observação da equipe:</b> {i.observation}</p>}
      <p className="bc-meta cell-sub">{i.confidence ? CONF_TXT[i.confidence] : 'confiança não informada'}{i.edited ? ` · editado${i.editedByName ? ` por ${i.editedByName}` : ''}` : ''}{i.notInLatest ? ' · não reapareceu na base mais recente (decisão anterior mantida)' : ''}</p>
      {extra}
      <ItemExplanation i={i} names={names} defaultOpen={defaultExplain} />
      <ItemActions i={i} />
    </li>
  );
}

/**
 * Linha compacta (fichas e listas longas) — IM-XPR-006 (IM-UX-142): a linha inteira é um botão de expandir
 * ([conteúdo flexível] [status sem quebra] [chevron]); ao abrir, fonte, natureza, confiança, data, evidência,
 * justificativa e as ações ficam na própria linha (sem dezenas de links "Detalhes" e sem outra página).
 */
export function ItemRow({ i, label }: { i: BattlecardItemDto; label?: string }) {
  const { names } = useCard();
  const [open, setOpen] = useState(false);
  const id = `bc-row-${i.id}`;
  const ev = itemEvidence(i);
  const newest = ev.shown.map((x) => x.evidence.date).filter((d): d is string => !!d).sort().at(-1) ?? null;
  return (
    <li className={`bc-row-item${open ? ' open' : ''}`}>
      <button type="button" className="bc-row-toggle" aria-expanded={open} aria-controls={id} onClick={() => setOpen((x) => !x)}>
        <span className="bc-row-text">{label ?? i.content}</span>
        <StatusChip i={i} />
        <span className="bc-chevron" aria-hidden="true" />
      </button>
      {open && (
        <div id={id} className="bc-row-detail">
          <dl className="bc-row-facts">
            <div><dt>Natureza</dt><dd>{BATTLECARD_CLASSIFICATION_LABEL[i.classification]}</dd></div>
            <div><dt>Confiança</dt><dd>{i.confidence ? CONF_TXT[i.confidence] : 'não informada'}</dd></div>
            <div><dt>Coleta</dt><dd>{newest ? fmtDate(newest) : 'sem data registrada'}</dd></div>
            <div><dt>Fonte</dt><dd>{statusDimensions(i).source}</dd></div>
          </dl>
          <ItemExplanation i={i} names={names} defaultOpen />
          <ItemActions i={i} />
        </div>
      )}
    </li>
  );
}
